import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { trackClick } from '../utils/analytics';

const { FiChevronLeft, FiNavigation, FiBriefcase, FiShoppingBag, FiActivity, FiTruck, FiCreditCard, FiSmile, FiCamera } = FiIcons;

const categories = [
  { id: 'government_public', label: 'Govt', icon: FiBriefcase, color: 'text-blue-900', subs: [ {id: 'police', label: 'Police'}, {id: 'fire', label: 'Fire'}, {id: 'city_hall', label: 'City Hall'} ] },
  { id: 'food_essentials', label: 'Food', icon: FiShoppingBag, color: 'text-orange-600', subs: [ {id: 'grocery', label: 'Grocery'}, {id: 'restaurants', label: 'Eats'}, {id: 'bakery', label: 'Bakery'} ] },
  { id: 'health_emergency', label: 'Health', icon: FiActivity, color: 'text-rose-600', subs: [ {id: 'hospital', label: 'Hospital'}, {id: 'pharmacy', label: 'Pharmacy'}, {id: 'clinic', label: 'Clinic'} ] },
  { id: 'tourist_attractions', label: 'Attractions', icon: FiCamera, color: 'text-amber-600', subs: [ {id: 'landmark', label: 'Landmarks'}, {id: 'museum', label: 'Museums'}, {id: 'park', label: 'Parks'}, {id: 'viewpoint', label: 'Viewpoints'} ] },
  { id: 'transport_mobility', label: 'Transpo', icon: FiTruck, color: 'text-blue-600', subs: [ {id: 'gas_station', label: 'Gas'}, {id: 'bus_terminal', label: 'Bus'} ] },
  { id: 'finance_bills', label: 'Finance', icon: FiCreditCard, color: 'text-emerald-700', subs: [ {id: 'bank', label: 'Bank'}, {id: 'atm', label: 'ATM'} ] },
  { id: 'lifestyle_personal', label: 'Style', icon: FiSmile, color: 'text-pink-600', subs: [ {id: 'salon', label: 'Salon'}, {id: 'gym', label: 'Gym'} ] }
];

const CategoryGrid = ({ onSearch, disabled }) => {
  const [selectedMain, setSelectedMain] = useState(null);

  const handleMainSelect = (cat) => {
    trackClick(`Category: ${cat.label}`, 'Explore');
    setSelectedMain(cat);
  };

  const handleSubSelect = (subId, label) => {
    trackClick(`SubCategory: ${label}`, 'Explore');
    onSearch(subId);
  };

  return (
    <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] p-5 sm:p-8 high-contrast-shadow mb-8 border border-slate-200 mx-auto w-full">
      <div className="flex justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center gap-2 sm:gap-3">
          {selectedMain && (
            <button 
              onClick={() => { trackClick('Back to Categories', 'Explore'); setSelectedMain(null); }} 
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="text-xl text-slate-950" />
            </button>
          )}
          <h3 className="text-xl sm:text-2xl font-black text-slate-950 tracking-tight">
            {selectedMain ? selectedMain.label : 'Explore Local'}
          </h3>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!selectedMain ? (
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {categories.map((cat) => (
              <button 
                key={cat.id} 
                disabled={disabled}
                onClick={() => handleMainSelect(cat)}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-5 rounded-2xl sm:rounded-3xl border-2 border-slate-100 transition-all hover:border-indigo-600 hover:shadow-lg bg-white group"
              >
                <div className={`p-2 sm:p-3 rounded-xl bg-slate-50 border border-slate-100 group-hover:bg-indigo-50 ${cat.color} transition-colors flex-shrink-0`}>
                  <SafeIcon icon={cat.icon} className="text-lg sm:text-xl" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <span className="font-black text-[10px] sm:text-xs text-slate-950 uppercase tracking-tight block truncate">{cat.label}</span>
                  <span className="text-[8px] sm:text-[9px] font-bold text-slate-600 uppercase tracking-widest block opacity-60">Browse</span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {selectedMain.subs.map((sub) => (
                <button 
                  key={sub.id} 
                  disabled={disabled}
                  onClick={() => handleSubSelect(sub.id, sub.label)}
                  className="flex flex-col items-start gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-white border-2 border-slate-100 hover:border-indigo-600 transition-all group"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-slate-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                    <SafeIcon icon={FiNavigation} className="text-[10px] sm:text-xs" />
                  </div>
                  <span className="font-black text-slate-950 text-xs sm:text-sm tracking-tight truncate w-full text-left">{sub.label}</span>
                </button>
              ))}
            </div>
            <button 
              onClick={() => handleSubSelect(selectedMain.id, `All ${selectedMain.label}`)}
              className="w-full py-4 sm:py-5 rounded-xl sm:rounded-2xl bg-slate-950 text-white font-black text-[9px] sm:text-[10px] uppercase tracking-widest active:scale-[0.98] transition-transform"
            >
              Search All {selectedMain.label}
            </button>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CategoryGrid;